import React, { useState } from 'react';
import { Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Exercise, ExerciseType, GroupingType, PrescriptionType } from '../../../types/program';

interface ExerciseEditorProps {
  exercise: Exercise;
  exerciseIndex: number;
  onUpdate: (exerciseIndex: number, updatedExercise: Exercise) => void;
  onDelete: (exerciseIndex: number) => void;
}

const exerciseTypeOptions: ExerciseType[] = ['strength', 'cardio', 'mobility', 'conditioning', 'skill', 'recovery'];
const prescriptionOptions: PrescriptionType[] = ['reps', 'rep_range', 'time', 'distance', 'calories', 'interval', 'amrap', 'emom', 'for_time'];
const groupingOptions: GroupingType[] = ['single', 'superset', 'circuit', 'block'];

const ExerciseEditor: React.FC<ExerciseEditorProps> = ({
  exercise,
  exerciseIndex,
  onUpdate,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const resolvedType = exercise.exerciseType || 'strength';
  const resolvedPrescription = exercise.prescriptionType || 'reps';
  const resolvedGrouping = exercise.grouping || 'single';

  const handleChange = (field: keyof Exercise, value: any) => {
    onUpdate(exerciseIndex, { ...exercise, [field]: value });
  };

  const handleTargetsChange = (field: string, value: any) => {
    onUpdate(exerciseIndex, {
      ...exercise,
      targets: {
        ...exercise.targets,
        [field]: value,
      },
    });
  };

  const notesLength = exercise.notes?.length || 0;
  const maxNotesLength = 500;

  const showRepFields = resolvedPrescription === 'reps' || resolvedPrescription === 'rep_range';
  const showTimeFields = ['time', 'interval', 'amrap', 'emom', 'for_time'].includes(resolvedPrescription);
  const showDistanceField = resolvedPrescription === 'distance';
  const showCaloriesField = resolvedPrescription === 'calories';
  const showRoundsField = ['interval', 'amrap', 'emom'].includes(resolvedPrescription);
  const showGroupIdField = resolvedGrouping !== 'single';
  const showTempoField = resolvedType === 'strength' || resolvedType === 'skill' || showRepFields;

  const formatLabel = (value: string) =>
    value
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const prescriptionGuideTitle = (() => {
    switch (resolvedPrescription) {
      case 'rep_range':
        return 'Strength Range Builder';
      case 'time':
        return 'Timed Hold Builder';
      case 'distance':
        return 'Distance Builder';
      case 'calories':
        return 'Calories Builder';
      case 'interval':
        return 'Interval Builder';
      case 'amrap':
        return 'AMRAP Builder';
      case 'emom':
        return 'EMOM Builder';
      case 'for_time':
        return 'For-Time Builder';
      default:
        return 'Repetition Builder';
    }
  })();

  const prescriptionGuideDescription = (() => {
    switch (resolvedPrescription) {
      case 'rep_range':
        return 'Use rep ranges, tempo, and rest to prescribe progressive strength work.';
      case 'time':
        return 'Best for holds, carries, mobility, and recovery work where duration leads.';
      case 'distance':
        return 'Great for runs, sled pushes, rows, and cardio pieces driven by distance.';
      case 'calories':
        return 'Use machine output targets for bikes, rowers, and calorie-based finishers.';
      case 'interval':
        return 'Define rounds, work duration, and rest to create a structured interval block.';
      case 'amrap':
        return 'Use rounds or a time cap for max-output conditioning blocks.';
      case 'emom':
        return 'Create minute-by-minute repeatable work with clear duration and round targets.';
      case 'for_time':
        return 'Set a time cap and let the athlete race the clock to finish.';
      default:
        return 'Use sets, reps, and rest for standard gym programming.';
    }
  })();

  const setsLabel = ['interval', 'amrap', 'emom'].includes(resolvedPrescription) ? 'Sets / Blocks' : 'Sets';
  const repsLabel = resolvedPrescription === 'rep_range' ? 'Rep Range' : 'Reps';
  const restLabel = resolvedPrescription === 'interval' ? 'Rest Between Rounds' : 'Rest Time (seconds)';
  const timeLabel = (() => {
    switch (resolvedPrescription) {
      case 'interval':
        return 'Work Duration (sec)';
      case 'emom':
        return 'Minute Window (sec)';
      case 'for_time':
      case 'amrap':
        return 'Time Cap (sec)';
      default:
        return 'Duration (sec)';
    }
  })();
  const roundsLabel = (() => {
    switch (resolvedPrescription) {
      case 'amrap':
        return 'Target Rounds';
      case 'emom':
        return 'Minutes / Rounds';
      default:
        return 'Rounds';
    }
  })();

  const quickOptionValues = (() => {
    switch (resolvedPrescription) {
      case 'interval':
        return { time: ['20', '30', '45', '60'], rounds: ['3', '4', '5', '6'] };
      case 'emom':
        return { time: ['60', '300', '600'], rounds: ['5', '10', '12'] };
      case 'for_time':
        return { time: ['300', '600', '900'], rounds: [] };
      case 'amrap':
        return { time: ['300', '600', '900'], rounds: ['3', '5', '8'] };
      default:
        return { time: ['30', '45', '60', '90'], rounds: ['3', '4', '5'] };
    }
  })();

  const groupingGuideTitle = (() => {
    switch (resolvedGrouping) {
      case 'superset':
        return 'Superset Link';
      case 'circuit':
        return 'Circuit Link';
      case 'block':
        return 'Block Link';
      default:
        return 'Single Exercise';
    }
  })();

  const groupingGuideDescription = (() => {
    switch (resolvedGrouping) {
      case 'superset':
        return 'Use the same group ID on paired exercises so they stay linked when the workout is executed.';
      case 'circuit':
        return 'Use a shared circuit label to bundle stations into one continuous conditioning loop.';
      case 'block':
        return 'Use a shared block label to keep multiple exercises inside one training block.';
      default:
        return 'This exercise stands alone with no shared grouping.';
    }
  })();

  const groupIdOptions = (() => {
    switch (resolvedGrouping) {
      case 'superset':
        return ['A1', 'A2', 'B1', 'B2'];
      case 'circuit':
        return ['Circuit A', 'Circuit B', 'Circuit C'];
      case 'block':
        return ['Block A', 'Block B', 'Block C'];
      default:
        return [] as string[];
    }
  })();


  return (
    <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-[10px] p-5 mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-[#1F1F1F] rounded-md flex items-center justify-center">
            <span className="text-[13px] font-bold text-[#23F47C]">{exerciseIndex + 1}</span>
          </div>
          <input
            type="text"
            value={exercise.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Exercise name *"
            className="bg-transparent border-none text-white text-base font-semibold focus:outline-none placeholder-[#5F5F5F] min-w-[200px]"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-9 h-9 bg-transparent border border-[#1F1F1F] rounded-lg flex items-center justify-center text-[#8F8F8F] hover:bg-[#1F1F1F] hover:text-white transition-all duration-200"
          >
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          <button
            onClick={() => onDelete(exerciseIndex)}
            className="w-9 h-9 bg-transparent border border-[#1F1F1F] rounded-lg flex items-center justify-center text-[#8F8F8F] hover:bg-[rgba(255,71,87,0.1)] hover:border-[#FF4757] hover:text-[#FF4757] transition-all duration-200"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="col-span-2 rounded-xl border border-[#1F1F1F] bg-[#141414] p-4">
            <div className="text-sm font-semibold text-white">{prescriptionGuideTitle}</div>
            <p className="mt-2 text-sm leading-6 text-[#8F8F8F]">{prescriptionGuideDescription}</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-white mb-2">Exercise Type</label>
            <select
              value={resolvedType}
              onChange={(e) => handleChange('exerciseType', e.target.value as ExerciseType)}
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            >
              {exerciseTypeOptions.map((option) => (
                <option key={option} value={option}>{formatLabel(option)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Prescription</label>
            <select
              value={resolvedPrescription}
              onChange={(e) => handleChange('prescriptionType', e.target.value as PrescriptionType)}
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            >
              {prescriptionOptions.map((option) => (
                <option key={option} value={option}>{formatLabel(option)}</option>
              ))}
            </select>
          <div>
          </div>
            <label className="block text-sm font-semibold text-white mb-2">Grouping</label>
            <select
              value={resolvedGrouping}
              onChange={(e) => onUpdate(exerciseIndex, {
                ...exercise,
                grouping: e.target.value as GroupingType,
                groupId: e.target.value === 'single' ? undefined : exercise.groupId,
              })}
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            >
              {groupingOptions.map((option) => (
                <option key={option} value={option}>{formatLabel(option)}</option>
              ))}
            </select>
          </div>

          {showGroupIdField && (
            <div className="col-span-2 rounded-xl border border-[#1F1F1F] bg-[#141414] p-4">
              <div className="text-sm font-semibold text-white">{groupingGuideTitle}</div>
              <p className="mt-2 text-sm leading-6 text-[#8F8F8F]">{groupingGuideDescription}</p>
              <div className="mt-4">
                <label className="block text-sm font-semibold text-white mb-2">Group ID</label>
                <input
                  type="text"
                  value={exercise.groupId || ''}
                  onChange={(e) => handleChange('groupId', e.target.value)}
                  placeholder={resolvedGrouping === 'superset' ? 'A1' : resolvedGrouping === 'circuit' ? 'Circuit A' : 'Block A'}
                  className="w-full px-4 py-3 bg-[#101010] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
                />
                {groupIdOptions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {groupIdOptions.map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleChange('groupId', value)}
                        className="rounded-full border border-[#1F1F1F] bg-[#101010] px-3 py-1.5 text-xs font-medium text-[#BDBDBD] transition hover:border-[#23F47C] hover:text-white"
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-white mb-2">Order</label>
            <input
              type="number"
              value={exercise.order || 0}
              onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-semibold text-white mb-2">Description</label>
            <textarea
              value={exercise.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Exercise description"
              rows={2}
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder-[#5F5F5F] focus:border-[#23F47C] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{setsLabel}</label>
            <input
              type="number"
              value={exercise.sets}
              onChange={(e) => handleChange('sets', parseInt(e.target.value) || 0)}
              min="0"
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>

          {showRepFields && (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">{repsLabel}</label>
              <input
                type="text"
                value={exercise.reps}
                onChange={(e) => handleChange('reps', e.target.value)}
                placeholder={resolvedPrescription === 'rep_range' ? '8-12' : '10'}
                className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-white mb-2">{restLabel}</label>
            <input
              type="number"
              value={exercise.restTimeSec}
              onChange={(e) => handleChange('restTimeSec', parseInt(e.target.value) || 0)}
              min="0"
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>

          {showTimeFields && (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">{timeLabel}</label>
              <input
                type="number"
                value={exercise.targets?.durationSec || 0}
                onChange={(e) => handleTargetsChange('durationSec', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                {quickOptionValues.time.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleTargetsChange('durationSec', parseInt(value) || 0)}
                    className="rounded-full border border-[#1F1F1F] bg-[#101010] px-3 py-1.5 text-xs font-medium text-[#BDBDBD] transition hover:border-[#23F47C] hover:text-white"
                  >
                    {value}s
                  </button>
                ))}
              </div>
            </div>
          )}

          {showDistanceField && (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Distance (m)</label>
              <input
                type="number"
                value={exercise.targets?.distanceMeters || 0}
                onChange={(e) => handleTargetsChange('distanceMeters', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
              />
            </div>
          )}

          {showCaloriesField && (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Calories</label>
              <input
                type="number"
                value={exercise.targets?.calories || 0}
                onChange={(e) => handleTargetsChange('calories', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
              />
            </div>
          )}

          {showRoundsField && (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">{roundsLabel}</label>
              <input
                type="number"
                value={exercise.targets?.rounds || 0}
                onChange={(e) => handleTargetsChange('rounds', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                {quickOptionValues.rounds.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleTargetsChange('rounds', parseInt(value) || 0)}
                    className="rounded-full border border-[#1F1F1F] bg-[#101010] px-3 py-1.5 text-xs font-medium text-[#BDBDBD] transition hover:border-[#23F47C] hover:text-white"
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          )}

          {showTempoField && (
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Tempo</label>
              <input
                type="text"
                value={exercise.tempo || exercise.targets?.tempo || ''}
                onChange={(e) => {
                  handleChange('tempo', e.target.value);
                  handleTargetsChange('tempo', e.target.value);
                }}
                placeholder="2-0-2-0"
                className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
              />
            </div>
          )}

          <div className="col-span-2">
            <label className="block text-sm font-semibold text-white mb-2">Video URL</label>
            <input
              type="url"
              value={exercise.videoUrl || exercise.media?.videoUrl || ''}
              onChange={(e) => {
                handleChange('videoUrl', e.target.value);
                onUpdate(exerciseIndex, {
                  ...exercise,
                  videoUrl: e.target.value,
                  media: {
                    ...exercise.media,
                    videoUrl: e.target.value,
                  },
                });
              }}
              placeholder="https://example.com/video.mp4"
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-semibold text-white mb-2">
              Notes <span className="text-[#8F8F8F] font-normal ml-2">({notesLength}/{maxNotesLength})</span>
            </label>
            <textarea
              value={exercise.notes}
              onChange={(e) => {
                if (e.target.value.length <= maxNotesLength) {
                  handleChange('notes', e.target.value);
                  onUpdate(exerciseIndex, {
                    ...exercise,
                    notes: e.target.value,
                    coaching: {
                      ...exercise.coaching,
                      notes: e.target.value,
                    },
                  });
                }
              }}
              placeholder="Special instructions or notes for this exercise"
              rows={3}
              maxLength={maxNotesLength}
              className="w-full px-4 py-3 bg-[#141414] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-[#23F47C] focus:outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseEditor;





